import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import ProductForm from '../components/ProductForm';
import StockAdjustmentModal from '../components/StockAdjustmentModal';
import api from '../lib/api';

const compareNonNegativeDecimalStrings = (leftValue, rightValue) => {
  const normalize = (value) => {
    const [wholePart = '0', fractionalPart = ''] = String(value).split('.');
    return {
      whole: wholePart.replace(/^0+(?=\d)/, '') || '0',
      fraction: fractionalPart.replace(/0+$/, ''),
    };
  };

  const left = normalize(leftValue);
  const right = normalize(rightValue);
  if (left.whole.length !== right.whole.length) {
    return left.whole.length < right.whole.length ? -1 : 1;
  }
  if (left.whole !== right.whole) {
    return left.whole < right.whole ? -1 : 1;
  }

  const width = left.fraction.length > right.fraction.length
    ? left.fraction.length
    : right.fraction.length;
  const leftFraction = left.fraction.padEnd(width, '0');
  const rightFraction = right.fraction.padEnd(width, '0');
  if (leftFraction === rightFraction) return 0;
  return leftFraction < rightFraction ? -1 : 1;
};

const Inventory = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);
  const limit = 50;

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      navigate('/login');
      return;
    }

    setUser(JSON.parse(storedUser));
    fetchProducts();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, categoryFilter, page]);

  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/products?limit=1000');
      const items = response.data.items || response.data.products || [];
      const uniqueCategories = [...new Set(items.map(p => p.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);

      const response = await api.get(`/products?${params}`);
      const items = response.data.items || response.data.products || [];
      setProducts(items);
      setTotal(response.data.total_count ?? response.data.total ?? items.length);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setPage(1);
    setShowDropdown(false);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(value);
      if (value.trim()) setShowDropdown(true);
    }, 300);
  };

  const handleSelectProduct = (product) => {
    setSearchInput(product.name);
    setSearch(product.name);
    setShowDropdown(false);
    setPage(1);
  };

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setShowProductForm(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setShowProductForm(true);
  };

  const handleAdjustStock = (product) => {
    setSelectedProduct(product);
    setShowStockModal(true);
  };

  const handleToggleActive = async (product) => {
    if (!confirm(`Are you sure you want to ${product.is_active ? 'deactivate' : 'activate'} this product?`)) {
      return;
    }

    try {
      await api.put(`/products/${product.id}`, {
        is_active: !product.is_active
      });
      fetchProducts();
    } catch (error) {
      console.error('Error toggling product status:', error);
      alert('Failed to update product status');
    }
  };

  const handleFormSuccess = () => {
    setShowProductForm(false);
    setSelectedProduct(null);
    fetchProducts();
  };

  const handleStockAdjustmentSuccess = () => {
    setShowStockModal(false);
    setSelectedProduct(null);
    fetchProducts();
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const totalPages = Math.ceil(total / limit);

  const dropdownProducts = products.slice(0, 8);

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="inventory-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
        isSidebarOpen ? 'md:ml-64' : 'md:ml-0'
      }`}>
        <Navbar user={user} toggleSidebar={toggleSidebar} />

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#09090B]" data-testid="inventory-title">
                Inventory
              </h1>
              <p className="text-sm text-[#52525B] mt-1">{total} products</p>
            </div>
            <button
              onClick={handleAddProduct}
              className="bg-[#1E40AF] text-white hover:bg-[#1e3a8a] rounded-xl px-4 py-2 font-medium transition-colors flex items-center gap-2"
              data-testid="add-product-button"
            >
              <Plus className="w-5 h-5" />
              Add Product
            </button>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-sm shadow-sm mb-6 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative" ref={searchRef}>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#52525B] pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by name or SKU..."
                  className="w-full pl-10 pr-4 py-2 border border-[#e5e7eb] rounded-sm focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0]"
                  value={searchInput}
                  onChange={handleSearchChange}
                  onFocus={() => { if (searchInput.trim() && products.length > 0) setShowDropdown(true); }}
                  data-testid="search-input"
                />
                {showDropdown && dropdownProducts.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-[#e5e7eb] rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {dropdownProducts.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleSelectProduct(product)}
                        className="w-full flex items-center justify-between px-4 py-2.5 text-sm border-b border-[#e5e7eb] last:border-0 hover:bg-[#f9fafb] text-left"
                      >
                        <div>
                          <p className="font-medium text-[#1a1a2e]">{product.name}</p>
                          <p className="text-xs text-[#9ca3af]">{product.sku} • Stock: {product.current_stock} {product.unit}</p>
                        </div>
                        <span className="text-[#4A7AE0] font-medium text-xs shrink-0 ml-3">
                          Rs. {parseFloat(product.selling_price).toLocaleString()}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#52525B] pointer-events-none" />
                <select
                  className="w-full pl-10 pr-4 py-2 border border-[#e5e7eb] rounded-sm focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0]"
                  value={categoryFilter}
                  onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                  data-testid="category-filter"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-[#52525B]">Loading products...</div>
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-sm shadow-sm p-12 text-center">
              <p className="text-[#52525B] mb-4">No products found</p>
              <button
                onClick={handleAddProduct}
                className="bg-[#1E40AF] text-white hover:bg-[#1e3a8a] rounded-xl px-4 py-2 font-medium transition-colors"
              >
                Add Your First Product
              </button>
            </div>
          ) : (
            <>
              <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
                <table className="w-full min-w-[600px] text-sm text-left" data-testid="products-table">
                  <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                    <tr>
                      <th className="py-3 px-4">SKU</th>
                      <th className="py-3 px-4">Name</th>
                      <th className="py-3 px-4">Category</th>
                      <th className="py-3 px-4">Unit</th>
                      <th className="py-3 px-4">Current Stock</th>
                      <th className="py-3 px-4">Reorder Level</th>
                      <th className="py-3 px-4">Selling Price</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                        <td className="py-3 px-4 font-medium text-[#1a1a2e]">{product.sku}</td>
                        <td className="py-3 px-4 text-[#1a1a2e]">{product.name}</td>
                        <td className="py-3 px-4 text-[#6b7280]">{product.category || '-'}</td>
                        <td className="py-3 px-4 text-[#1a1a2e]">{product.unit}</td>
                        <td className="py-3 px-4">
                          <span className={compareNonNegativeDecimalStrings(product.current_stock, product.reorder_level) <= 0 ? 'text-[#EF4444] font-semibold' : 'text-[#09090B]'}>
                            {product.current_stock}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-[#6b7280]">{product.reorder_level}</td>
                        <td className="py-3 px-4 text-[#1a1a2e]">Rs. {parseFloat(product.selling_price).toLocaleString()}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                            product.is_active ? 'bg-[#DBEAFE] text-[#1E40AF]' : 'bg-[#FEE2E2] text-[#991B1B]'
                          }`}>
                            {product.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="inline-flex items-center px-3 py-1.5 rounded-lg border border-[#1E40AF] text-[#1E40AF] hover:bg-[#DBEAFE] text-xs font-medium"
                              data-testid={`edit-product-${product.id}`}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleAdjustStock(product)}
                              className="inline-flex items-center px-3 py-1.5 rounded-lg border border-[#1E40AF] text-[#1E40AF] hover:bg-[#DBEAFE] text-xs font-medium"
                              data-testid={`adjust-stock-${product.id}`}
                            >
                              Adjust Stock
                            </button>
                            <button
                              onClick={() => handleToggleActive(product)}
                              className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-xs font-medium ${
                                product.is_active ? 'border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2]' : 'border-[#4A7AE0] text-[#4A7AE0] hover:bg-[#DBEAFE]'
                              }`}
                              data-testid={`toggle-active-${product.id}`}
                            >
                              {product.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border border-[#e5e7eb] rounded-xl text-sm font-medium text-[#1a1a2e] hover:bg-[#f9fafb] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-[#52525B]">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 border border-[#e5e7eb] rounded-xl text-sm font-medium text-[#1a1a2e] hover:bg-[#f9fafb] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {showProductForm && (
        <ProductForm
          product={selectedProduct}
          onClose={() => setShowProductForm(false)}
          onSuccess={handleFormSuccess}
        />
      )}

      {showStockModal && selectedProduct && (
        <StockAdjustmentModal
          product={selectedProduct}
          onClose={() => setShowStockModal(false)}
          onSuccess={handleStockAdjustmentSuccess}
        />
      )}
    </div>
  );
};

export default Inventory;
